import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { usePeriod } from "../lib/PeriodContext";
import { PageTimeline } from "../components/PageTimeline";
import api from "../lib/api";
import { fmtINR, fmtPct } from "../lib/format";
import { Sparkles, AlertTriangle, TrendingUp, TrendingDown, Minus, X, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";
import { toast } from "sonner";

const sevColors = {
  critical: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-600", text: "text-red-800" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600", text: "text-amber-800" },
  positive: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-600", text: "text-emerald-800" },
};

export default function StrategicInsights() {
  const { asOf, projectsQS } = usePeriod();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [narrLoading, setNarrLoading] = useState(false);
  const [playbook, setPlaybook] = useState(null); // { category, headline, text }

  useEffect(() => {
    if (!asOf) return;
    api.get(`/kpi/strategic-insights?as_of=${asOf}&period=YTD${projectsQS}`).then((r) => setData(r.data));
  }, [asOf, projectsQS]);

  const fetchInsights = async () => {
    setInsightsLoading(true);
    try {
      const { data: d } = await api.post("/ai/insights", { context: { period: "YTD", as_of: asOf, kpis: data?.kpi_snapshot } });
      setInsights(d.insights || []);
    } catch (e) { toast.error("Failed to load insights"); }
    finally { setInsightsLoading(false); }
  };

  const fetchNarrative = async () => {
    setNarrLoading(true);
    try {
      const { data: d } = await api.post("/ai/narrative", { context: { period: "YTD", as_of: asOf, kpis: data?.kpi_snapshot } });
      setNarrative(d.narrative);
    } catch (e) { toast.error("Narrative failed"); }
    finally { setNarrLoading(false); }
  };

  const openPlaybook = async (ins) => {
    setPlaybook({ category: ins.category, headline: ins.headline, text: "Generating playbook..." });
    try {
      const { data: d } = await api.post("/ai/playbook", { category: ins.category, headline: ins.headline });
      setPlaybook({ category: ins.category, headline: ins.headline, text: d.playbook });
    } catch (e) {
      setPlaybook({ category: ins.category, headline: ins.headline, text: "Unable to generate playbook." });
    }
  };

  if (!data) return <><Header title="Strategic Insights" /><div className="p-6">Loading...</div></>;

  const k = data.kpi_snapshot;
  const trend = data.trend;

  const dirIcon = (d) => d === "up" ? <TrendingUp size={14} className="text-emerald-600" /> :
                       d === "down" ? <TrendingDown size={14} className="text-red-600" /> :
                       <Minus size={14} className="text-slate-500" />;

  return (
    <>
      <Header title="Strategic Insights" subtitle="Executive Intelligence Brief" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <PageTimeline fyLabel={data.fy_label} />
        {/* Executive Intelligence Brief */}
        <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200/60 rounded-md p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-amber-700" />
              <div className="text-sm font-bold">Executive Intelligence Brief</div>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{data.fy_label}</span>
            </div>
            <button
              onClick={fetchInsights}
              disabled={insightsLoading}
              className="text-xs px-3 py-1.5 bg-vexen-sidebar text-white rounded-md hover:bg-slate-800 disabled:opacity-60"
              data-testid="generate-insights-btn"
            >
              {insightsLoading ? "Generating..." : insights ? "Regenerate" : "Generate Brief"}
            </button>
          </div>
          {insights ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {insights.map((ins, i) => {
                const c = sevColors[ins.severity] || sevColors.warning;
                return (
                  <div key={i} className={`rounded-md border p-4 ${c.bg} ${c.border}`} data-testid={`insight-card-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={12} className={c.icon} />
                      <span className={`text-[10px] uppercase tracking-wider font-mono ${c.text}`}>{ins.category}</span>
                    </div>
                    <div className="text-sm font-bold mb-2 text-slate-900">{ins.headline}</div>
                    <div className="text-xs text-slate-700 leading-relaxed mb-3">{ins.insight}</div>
                    <div className="flex items-center justify-between border-t border-current/10 pt-2">
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{ins.metric_label}</div>
                        <div className="font-mono font-bold text-sm">{ins.metric_value}</div>
                      </div>
                      <button
                        onClick={() => openPlaybook(ins)}
                        className="text-[10px] uppercase tracking-wider font-mono text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
                        data-testid={`playbook-btn-${i}`}
                      >
                        Playbook <ArrowRight size={11} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-slate-600">Tap "Generate Brief" to surface five executive-grade insights covering sales, collection, cancellations, target, and inventory.</div>
          )}
        </div>

        {/* Strategic Narrative */}
        <div className="bg-white rounded-md border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">AI Strategic Narrative</div>
            <button
              onClick={fetchNarrative}
              disabled={narrLoading}
              className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-60"
              data-testid="generate-narrative-btn"
            >
              {narrLoading ? "Generating..." : "Generate Narrative"}
            </button>
          </div>
          {narrative ? (
            <div className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">{narrative}</div>
          ) : (
            <div className="text-xs text-slate-500">Three-paragraph board-ready narrative covering diagnosis, risks, and recommended actions.</div>
          )}
        </div>

        {/* Management Summary - dark card */}
        <div className="rounded-md p-5 text-white" style={{ background: "#0F1D32" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-mono mb-3">Management Summary · {data.fy_label}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Net Sales</div><div className="font-mono font-bold text-xl mt-1">{fmtINR(k.net_sales, { short: true })}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Collection</div><div className="font-mono font-bold text-xl mt-1">{fmtINR(k.collection, { short: true })}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Coll Efficiency</div><div className="font-mono font-bold text-xl mt-1 text-gold">{fmtPct(k.collection_efficiency)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Blocked Units</div><div className="font-mono font-bold text-xl mt-1">{k.blocked_units}</div></div>
          </div>
        </div>

        {/* Trend + Efficiency */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Trend Analysis (MoM)</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Sales Direction</span>
                <span className="inline-flex items-center gap-1 font-semibold capitalize">{dirIcon(trend.sales_direction)} {trend.sales_direction}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Collection Direction</span>
                <span className="inline-flex items-center gap-1 font-semibold capitalize">{dirIcon(trend.collection_direction)} {trend.collection_direction}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Efficiency Metrics</div>
            <div className="space-y-3">
              {[
                { label: "Collection Efficiency", v: data.efficiency.collection_efficiency },
                { label: "Sales Achievement", v: data.efficiency.sales_achievement },
                { label: "Net Conversion", v: data.efficiency.net_conversion },
              ].map((m) => (
                <div key={m.label}>
                  <div className="flex justify-between text-xs">
                    <span>{m.label}</span>
                    <span className="font-mono font-semibold">{fmtPct(m.v)}</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full" style={{ width: `${Math.min(100, m.v)}%`, background: m.v > 75 ? "#059669" : m.v > 50 ? "#D97706" : "#DC2626" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Blocked aging chart */}
        <div className="bg-white rounded-md border border-slate-200 p-4">
          <div className="text-sm font-semibold mb-3">Blocked Units Aging</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.blocked_aging}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} name="Blocked Units" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Exceptions */}
        <div className="bg-white rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">Exception Reporting · Projects Needing Attention</div>
          <table className="w-full text-xs" data-testid="exceptions-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Project</th>
                <th className="text-right px-4 py-2">Target</th>
                <th className="text-right px-4 py-2">Actual</th>
                <th className="text-right px-4 py-2">Achievement</th>
              </tr>
            </thead>
            <tbody>
              {data.exceptions.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No exceptions — all projects within tolerance.</td></tr>
              ) : data.exceptions.map((r) => (
                <tr key={r.project_name} className="border-t border-slate-100">
                  <td className="px-4 py-2 truncate max-w-[260px]">{r.project_name}</td>
                  <td className="numeric-cell px-4 py-2">{fmtINR(r.target, { short: true })}</td>
                  <td className="numeric-cell px-4 py-2">{fmtINR(r.actual, { short: true })}</td>
                  <td className="numeric-cell px-4 py-2 font-semibold text-red-600">{fmtPct(r.achievement_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Playbook Modal */}
        {playbook && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPlaybook(null)} data-testid="playbook-modal">
            <div className="bg-white rounded-lg max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">Action Playbook · {playbook.category}</div>
                  <div className="text-base font-bold mt-1">{playbook.headline}</div>
                </div>
                <button onClick={() => setPlaybook(null)} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
              </div>
              <div className="p-6 text-sm leading-relaxed text-slate-700 whitespace-pre-line">
                {playbook.text}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
